import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, IconButton, ListItem, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { To } from 'react-router'
import { useNavigate } from 'react-router'

import type { DestinationDto } from '@hcr/api/optimizely'
import { COLORS, DividerWithLabel, ICONS, unit } from '@hcr/ui'
import { flow, getPropertyValue, group, isEmpty, isNotUndefined, sort } from '@hcr/utils'

import { useHoliday, useLastPath } from '../../../contexts'
import { useDestinationsQuery } from '../../../hooks'
import { Path } from '../../../models'
import { compareStringsIntl, isServicesPath, to } from '../../../utils'
import { LayoutBase, LayoutNavigationBackError500, Metadata } from '../../common'

import { Destination } from './common'
import { ContextMenuDestinationsLoading } from './ContextMenuDestinationsLoading'

export const ContextMenuDestinations: FC = () => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const navigate = useNavigate()
  const {
    data: { destination: holidayDestination },
    ...holiday
  } = useHoliday()

  const destinations = useDestinationsQuery({
    select: flow(
      getPropertyValue('destinations'),
      sort({ comparer: compareStringsIntl, asc: getPropertyValue('title') }),
      group(({ isSpa }) => (isSpa ? 'isSpa' : 'isNotSpa'))
    ),
  })

  const getBackNavigationPath = useCallback(
    (destinationId?: string): To => {
      if (isNotUndefined(destinationId) && isServicesPath(lastPath.root)) {
        return to([Path.Services, { destinationId }])
      }

      return lastPath.root
    },
    [lastPath.root]
  )

  const createDestination = useCallback(
    (destination: DestinationDto) => (
      <ListItem
        sx={{
          '&:not(:last-of-type)': {
            borderBottom: `thin solid ${COLORS.vaalea[600]}`,
          },
        }}
        disablePadding
        key={destination.destinationId}
      >
        <Destination
          description={destination.description}
          isActive={destination.destinationId === holidayDestination?.destinationId}
          onClick={() => {
            holiday.setDestination(destination)
            void navigate(getBackNavigationPath(destination.destinationId))
          }}
          title={destination.title}
        />
      </ListItem>
    ),
    [getBackNavigationPath, holiday, holidayDestination?.destinationId, navigate]
  )

  if (destinations.isError || (destinations.isSuccess && isEmpty(destinations.data))) {
    return <LayoutNavigationBackError500 to={getBackNavigationPath()} />
  }

  return (
    <LayoutBase>
      <Metadata title={t('page-title.context-menu-destinations')} />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: COLORS.hiekka[500],
        }}
      >
        <Box
          component='header'
          sx={{
            backgroundColor: COLORS.vaalea['60%'],
            padding: `0 0 ${unit(3.5)} ${unit(7)}`,
          }}
        >
          <Stack>
            <IconButton
              onClick={() => navigate(getBackNavigationPath())}
              aria-label={t('context-menu.close')}
              sx={{
                alignSelf: 'end',
                margin: `${unit(4.5)} ${unit(8)} 0 0`,
                padding: 0,
                fontSize: unit(6),
              }}
            >
              <FontAwesomeIcon icon={ICONS.farXmark} />
            </IconButton>
          </Stack>
          <Typography variant='headlineS'>{t('context-menu.holiday-club-destinations')}</Typography>
        </Box>
        <main>
          {destinations.isSuccess ? (
            <>
              {destinations.data.has('isSpa') && (
                <>
                  <DividerWithLabel>{t('context-menu.spa-hotels-holiday-houses-and-apartments')}</DividerWithLabel>
                  <Box>{destinations.data.get('isSpa')?.map(createDestination)}</Box>
                </>
              )}
              {destinations.data.has('isNotSpa') && (
                <>
                  <DividerWithLabel>{t('context-menu.holiday-houses-and-apartments')}</DividerWithLabel>
                  <Box>{destinations.data.get('isNotSpa')?.map(createDestination)}</Box>
                </>
              )}
            </>
          ) : (
            <ContextMenuDestinationsLoading />
          )}
        </main>
      </Box>
    </LayoutBase>
  )
}
